import React, { useState, useEffect } from 'react';
import { warrantyApi } from '../../services/api';

interface RmaRequest {
  id: string;
  rmaNumber: string;
  orderNumber?: string;
  component: string;
  issueType: string;
  description: string;
  status: string;
  adminNotes?: string;
  denialReason?: string;
  photoUrls: string[];
  dealerCompanyName?: string;
  warrantyCustomerName?: string;
  createdAt: string;
  updatedAt: string;
}

const componentLabels: Record<string, string> = {
  motor: 'Motor',
  fabric: 'Fabric',
  track: 'Track',
  housing: 'Housing',
  remote: 'Remote',
  other: 'Other',
};

const issueLabels: Record<string, string> = {
  manufacturing_defect: 'Manufacturing Defect',
  shipping_damage: 'Shipping Damage',
  motor_failure: 'Motor Failure',
  fabric_tear: 'Fabric Tear',
  track_binding: 'Track Binding',
  weather_damage: 'Weather Damage',
  other: 'Other',
};

export default function RmaQueuePage() {
  const [rmas, setRmas] = useState<RmaRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Selected RMA for review
  const [selectedRma, setSelectedRma] = useState<RmaRequest | null>(null);

  // Action form
  const [adminNotes, setAdminNotes] = useState('');
  const [denialReason, setDenialReason] = useState('');

  useEffect(() => {
    fetchRmas();
  }, [page]);

  const fetchRmas = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await warrantyApi.getRmaQueue({ page, pageSize: 20 });
      setRmas(response.data || []);
      setTotalPages(response.totalPages || 1);
      setTotal(response.total || 0);
    } catch (err: any) {
      setError(err.message || 'Failed to load RMA queue');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id: string) => {
    if (!window.confirm('Are you sure you want to approve this RMA?')) return;

    setActionLoading(id);
    try {
      await warrantyApi.approveRma(id, adminNotes || undefined);
      setSelectedRma(null);
      setAdminNotes('');
      fetchRmas();
    } catch (err: any) {
      alert(err.message || 'Failed to approve RMA');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeny = async (id: string) => {
    if (!denialReason.trim()) {
      alert('Please provide a reason for denial');
      return;
    }

    if (!window.confirm('Are you sure you want to deny this RMA?')) return;

    setActionLoading(id);
    try {
      await warrantyApi.denyRma(id, denialReason, adminNotes || undefined);
      setSelectedRma(null);
      setAdminNotes('');
      setDenialReason('');
      fetchRmas();
    } catch (err: any) {
      alert(err.message || 'Failed to deny RMA');
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">RMA Queue</h1>
        <p className="text-gray-500">Review pending RMA requests</p>
      </div>

      {/* Stats */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-center gap-3">
          <div className="text-yellow-600 text-2xl font-bold">{total}</div>
          <div className="text-yellow-800">pending RMA requests awaiting review</div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-500">Loading RMA queue...</p>
        </div>
      ) : rmas.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <div className="text-green-500 text-5xl mb-4">&#10003;</div>
          <h3 className="text-lg font-medium text-gray-900">Queue is Empty</h3>
          <p className="text-gray-500 mt-1">No pending RMA requests to review</p>
        </div>
      ) : (
        <>
          {/* RMA Cards */}
          <div className="space-y-4">
            {rmas.map((rma) => (
              <div key={rma.id} className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">{rma.rmaNumber}</h3>
                    <p className="text-gray-500">
                      Order: {rma.orderNumber || 'N/A'} | Dealer: {rma.dealerCompanyName || 'N/A'}
                    </p>
                  </div>
                  <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-sm font-medium rounded-full">
                    Pending Review
                  </span>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div>
                    <span className="text-gray-500 text-sm">Component</span>
                    <p className="font-medium">{componentLabels[rma.component]}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 text-sm">Issue Type</span>
                    <p className="font-medium">{issueLabels[rma.issueType]}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 text-sm">Customer</span>
                    <p className="font-medium">{rma.warrantyCustomerName || '-'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 text-sm">Submitted</span>
                    <p className="font-medium">{formatDate(rma.createdAt)}</p>
                  </div>
                </div>

                <div className="mb-4">
                  <span className="text-gray-500 text-sm">Description</span>
                  <p className="text-gray-700 bg-gray-50 p-3 rounded-lg mt-1">
                    {rma.description}
                  </p>
                </div>

                {/* Photos Preview */}
                {rma.photoUrls && rma.photoUrls.length > 0 && (
                  <div className="mb-4">
                    <span className="text-gray-500 text-sm">Photos ({rma.photoUrls.length})</span>
                    <div className="flex gap-2 mt-1 overflow-x-auto pb-2">
                      {rma.photoUrls.slice(0, 4).map((url, index) => (
                        <a key={index} href={url} target="_blank" rel="noopener noreferrer">
                          <img
                            src={url}
                            alt={`Issue ${index + 1}`}
                            className="w-20 h-20 object-cover rounded-lg hover:opacity-80"
                          />
                        </a>
                      ))}
                      {rma.photoUrls.length > 4 && (
                        <div className="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center text-gray-500">
                          +{rma.photoUrls.length - 4}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => {
                      setSelectedRma(rma);
                      setAdminNotes('');
                      setDenialReason('');
                    }}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium"
                  >
                    Review Details
                  </button>
                  <button
                    onClick={() => handleApprove(rma.id)}
                    disabled={actionLoading === rma.id}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50"
                  >
                    {actionLoading === rma.id ? 'Processing...' : 'Quick Approve'}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 border border-gray-300 rounded text-sm text-gray-700 disabled:opacity-50 hover:bg-gray-100"
              >
                Previous
              </button>
              <span className="text-sm text-gray-500">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1 border border-gray-300 rounded text-sm text-gray-700 disabled:opacity-50 hover:bg-gray-100"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Review Modal */}
      {selectedRma && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Review RMA Request</h2>
                  <p className="text-blue-600 font-medium">{selectedRma.rmaNumber}</p>
                </div>
                <button
                  onClick={() => setSelectedRma(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-6">
                {/* Request Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-gray-500 text-sm">Order Number</span>
                    <p className="font-medium">{selectedRma.orderNumber || '-'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 text-sm">Dealer</span>
                    <p className="font-medium">{selectedRma.dealerCompanyName || '-'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 text-sm">Component</span>
                    <p className="font-medium">{componentLabels[selectedRma.component]}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 text-sm">Issue Type</span>
                    <p className="font-medium">{issueLabels[selectedRma.issueType]}</p>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <span className="text-gray-500 text-sm">Description</span>
                  <p className="text-gray-700 bg-gray-50 p-3 rounded-lg mt-1">
                    {selectedRma.description}
                  </p>
                </div>

                {/* All Photos */}
                {selectedRma.photoUrls && selectedRma.photoUrls.length > 0 && (
                  <div>
                    <span className="text-gray-500 text-sm">Photos</span>
                    <div className="grid grid-cols-3 gap-2 mt-1">
                      {selectedRma.photoUrls.map((url, index) => (
                        <a key={index} href={url} target="_blank" rel="noopener noreferrer">
                          <img
                            src={url}
                            alt={`Issue ${index + 1}`}
                            className="w-full h-32 object-cover rounded-lg hover:opacity-80"
                          />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Admin Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Admin Notes (optional)
                  </label>
                  <textarea
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Add notes for internal reference..."
                  />
                </div>

                {/* Denial Reason */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Denial Reason (required if denying)
                  </label>
                  <textarea
                    value={denialReason}
                    onChange={(e) => setDenialReason(e.target.value)}
                    rows={2}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Reason for denial (will be sent to dealer)..."
                  />
                </div>
              </div>

              <div className="mt-6 flex gap-3 justify-end">
                <button
                  onClick={() => setSelectedRma(null)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeny(selectedRma.id)}
                  disabled={actionLoading === selectedRma.id}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:opacity-50"
                >
                  {actionLoading === selectedRma.id ? 'Processing...' : 'Deny RMA'}
                </button>
                <button
                  onClick={() => handleApprove(selectedRma.id)}
                  disabled={actionLoading === selectedRma.id}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50"
                >
                  {actionLoading === selectedRma.id ? 'Processing...' : 'Approve RMA'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
